import React, { useState } from "react";
import { toast } from "react-toastify";
import Table from "../../../../components/elements/table/Table";
import { setTitle } from "../../../../helpers/MetaTag";
import { handleFilterChange } from "../../../../helpers";
import DeleteModal from "../../../../components/common/DeleteModal";
import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import {
  faqDelete,
  faqOrdering,
  faqStatusChange,
} from "../../../../services/faqService";
import Switch from "../../../../components/form/Switch";
import Badge from "../../../../components/elements/Badge";
// import { useGetPermission } from "../../hooks/useGetPermission";
import Search from "../../../../components/form/Search";
import Button from "../../../../components/form/Button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { faqList } from "../../../../services/faqService";
import ManageFaqDrawer from "../../../../components/sections/admin/Faq/Drawer/ManageFaqDrawer";
import { truncateHtmlByWords } from "../../../../util/Flag";
import { lngDetect } from "../../../../helpers/lngDetect";
import { tableFilter } from "../../../../helpers/tableFilterOptions";
import Select from "../../../../components/form/select/Select";
import ActionBar from "../../../../components/elements/ActionBar";

const Faq = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "ordering",
  });

  const options = [
    {
      id: "userDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "User Delete",
    },
  ];

  const { data, error, isLoading, updateData, isFetching, refetch } = useQuery({
    queryKey: ["faq", filters, search],
    queryFn: async () => {
      const res = await faqList({
        ...filters,
        type: "faq",
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  //   FAQ status change handler =================>
  const { mutate: statusChangeHandler } = useMutation({
    mutationFn: async ({ id, status }) => {
      queryClient.setQueryData(["faq", filters, search], (state) => {
        if (state) {
          let stateData = JSON.parse(JSON.stringify(state?.data));
          stateData = (stateData || []).map((elm) =>
            elm?._id === id
              ? {
                  ...elm,
                  status: elm?.status === "active" ? "inActive" : "active",
                }
              : elm
          );
          return { ...state, data: stateData };
        }
      });
      const res = await faqStatusChange({
        ids: id,
        status: status,
      });
      return { res, payload: { id, status } };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status !== 200) {
        queryClient.setQueryData(["faq", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || []).map((elm) =>
              elm?._id === payload?.id
                ? {
                    ...elm,
                    status: elm?.status === "active" ? "inActive" : "active",
                  }
                : elm
            );
            return { ...state, data: stateData };
          }
        });
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const tableHeadData = [
    {
      _id: 2,
      name: "Question",
      align: "left",
      // isSort: true,
      // isFilter: false,
      // isFilterSearch: false,
      // onSort: (val) => {
      //   if (val === "DESC") {
      //     // setSort("-description");
      //   } else {
      //     // setSort("description");
      //   }
      // },
    },
    {
      _id: 3,
      name: "Answer Preview",
      align: "left",
      // isSort: false,
      // isFilter: false,
      // isFilterSearch: false,
    },
    {
      _id: 4,
      name: "Visibility",
      align: "left",
      ...tableFilter({
        filterKey: "visibility",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val }));
        },
        filterList: [
          { name: "private", value: "private", type: "checkbox" },
          { name: "public", value: "public", type: "checkbox" },
        ],
      }),
    },
    {
      _id: 5,
      name: "Status",
      align: "left",
      ...tableFilter({
        filterKey: "status",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val }));
        },
        filterList: [
          { name: "pending", value: "pending", type: "checkbox" },
          { name: "active", value: "active", type: "checkbox" },
          { name: "invited", value: "invited", type: "checkbox" },
          { name: "deactivated", value: "deactivated", type: "checkbox" },
          { name: "deleted", value: "deleted", type: "checkbox" },
          { name: "inactive", value: "inactive", type: "checkbox" },
        ],
      }),
    },
    {
      _id: 6,
      name: "Action",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            (
              <div
                dangerouslySetInnerHTML={{
                  __html: lngDetect(item?.title) || "-",
                }}
              ></div>
            ) || "-",
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            (
              <div
                dangerouslySetInnerHTML={{
                  __html: lngDetect(item?.excerpt) || "-",
                }}
              ></div>
            ) || "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="flex gap-2 items-center">
              <Badge
                type={
                  {
                    public: "success",
                    private: "info",
                    password: "warning",
                  }[item?.visibility] || "default"
                }
                label={lngDetect(
                  item?.visibility?.charAt(0).toUpperCase() +
                    item?.visibility?.slice(1)
                )}
              />
            </div>
          ),
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="flex gap-2 items-center">
              {/* <Switch
                className={
                  "!w-10 !h-5 before:!w-4 before:!h-4 peer-checked:before:!left-[22px]"
                }
                checked={item?.status === "active"}
                onChange={() => {
                  statusChangeHandler({
                    id: item?._id,
                    status: item?.status === "active" ? "inactive" : "active",
                  });
                }}
              /> */}
              <Badge
                type={
                  {
                    draft: "warning",
                    published: "success",
                    private: "info",
                    trash: "danger",
                  }[item?.status] || "default"
                }
                label={lngDetect(
                  item?.status?.charAt(0).toUpperCase() + item?.status?.slice(1)
                )}
              />
            </div>
          ),
        },

        {
          _id: 6,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: 1,
              name: "Edit",
              icon: "fa-regular fa-pen-to-square",
              onClick: () => {
                setModalOpen({ isOpen: true, data: item });
              },
              standout: false,
            },

            {
              _id: 2,
              name: "Delete",
              icon: "fa-regular fa-trash-can",
              standout: true,
              onClick: () => {
                setDeleteModal({
                  isOpen: true,
                  data: item,
                });
              },
            },
          ],
        },
      ],
    };
  });

  // add or edit FAQ =================>
  function faqManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          queryClient.setQueryData(["faq", filters, search], (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = [...stateData, res?.data];
              return { ...state, data: stateData };
            }
          });
          if (data?.metadata?.totalDocs > filters?.limit) {
            queryClient.invalidateQueries(["faq", filters, search]);
          }
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        queryClient.setQueryData(["faq", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = stateData?.map((item) =>
              item?._id === res?.data?._id ? { ...item, ...res?.data } : item
            );
            return { ...state, data: stateData };
          }
        });
        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["faq", filters, search]);
        }
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  //   FAQ delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await faqDelete({
        ids: Array.isArray(payload) ? payload : [payload],
        type: "faq",
      });
      return { res, payload: Array.isArray(payload) ? payload : [payload] };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        queryClient.setQueryData(["faq", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || [])?.filter(
              (item) => !payload?.includes(item?._id)
            );
            return { ...state, data: stateData };
          }
        });

        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["faq", filters, search]);
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  async function faqOrderingHandler(newData, oldData) {
    const ordering_data = newData?.map((item, i) => ({
      id: item?._id,
      ordering: i,
      type: "faq",
    }));
    const res = await faqOrdering({ ordering_data });
    if (res?.status !== 200) {
      queryClient.setQueryData(["faq", filters, search], (state) => {
        if (state) {
          return { ...state, data: oldData };
        }
      });
      toast.warning(res?.message || res?.error);
    }
  }

  function actionHandler(val) {
    switch (val) {
      case "userDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

      default:
        break;
    }
  }

  return (
    <div className="w-full flex flex-col pb-10 bg-white rounded-md">
      <div className="space-y-8 py-8 px-5">
        <div className="flex justify-between items-center border-b border-gray-300">
          <div className="flex flex-col gap-2 w-full">
            <div className="font-bold text-lg xl:text-2xl uppercase tracking-tighter font-Montserrat">
              Faq Management
            </div>
            <div className="text-sm xl:text-base h-10">
              Easily handle all FAQ actions: update FAQ info, email, status, and
              add new FAQ data.
            </div>
          </div>
          <div className="relative">
            <Button
              buttonClasses={
                "!bg-transparent border border-transparent !h-10 hover:!bg-primary/10 hover:!border-primary/10"
              }
              variant="secondary"
              buttonLabel={"Create New"}
              buttonLabelClasses={
                "font-medium !text-sm uppercase whitespace-nowrap"
              }
              buttonHasLink
              buttonFunction={() => setModalOpen({ isOpen: true, data: null })}
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-36">
              <LimitSelect
                value={filters?.limit}
                allValue={data?.metadata?.totalDocs}
                onChange={(val) => handleFilterChange("limit", val, setFilters)}
              />
            </div>
            <div className="w-72">
              <Search
                placeholder={"Search here"}
                search={(val) => {
                  setKeyword(val);
                  handleFilterChange("page", 1, setFilters);
                }}
              />
            </div>
            {selectedItems?.length > 0 && (
              <div className="flex items-center gap-2">
                <ActionBar options={options} onChange={actionHandler} />
                <span className="flex items-center rounded-full bg-secondary text-surface-01 text-xs px-3 py-1 gap-2">
                  {selectedItems?.length} Selected
                  <button
                    onClick={() => setSelectedItems([])}
                    type="button"
                    className="cursor-pointer"
                  >
                    <i className="fa-fw fa-light fa-xmark"></i>
                  </button>
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Select
                selectedValue={"Buyer"}
                dropdownData={[{ name: "Buyer", value: "Buyer" }]}
                // onChange={(val) => {
                //   console.log({ val });
                //   handleChange({ target: { name: "parent", value: val } });
                // }}
                // onScroll={(e) => {
                //   loadData(e, categories, setCategoryilters);
                // }}
              />
            </div>
            <div className="relative">
              <Select
                selectedValue={"Past 3 month"}
                dropdownData={[{ name: "Past 3 month", value: "Past 3 month" }]}
                // onChange={(val) => {
                //   console.log({ val });
                //   handleChange({ target: { name: "parent", value: val } });
                // }}
                // onScroll={(e) => {
                //   loadData(e, categories, setCategoryilters);
                // }}
              />
            </div>
          </div>
        </div>
        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={isLoading}
          pageCount={data?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          handleOrdering={(newData, oldData) => {
            faqOrderingHandler(newData, oldData);
          }}
          originalData={[...(data?.data || [])]}
          setOriginalData={(data) =>
            queryClient.setQueryData(["faq", filters, search], (state) => {
              if (state) {
                return { ...state, data: data };
              }
            })
          }
          isFetching={isFetching}
          isOrderingDisabled={false}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
          // selectable
        />
      </div>

      <ManageFaqDrawer
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={faqManageResponse}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default Faq;
